import { Command, ShiftData } from '@nextforce/api'
import { Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React from 'react'
import { ScrollView, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { CommandButtons } from 'shared/components/command_buttons'

interface Props {
  shiftData: ShiftData
  onCommandButtonPress(command: Command): Promise<void>
  commands: Command[]
  newBonusAmount: number
  setNewBonusAmount: (amount: number) => void
  error?: any
}

export default function EditCriticalShiftBonusView({
  shiftData,
  onCommandButtonPress,
  commands,
  newBonusAmount,
  setNewBonusAmount,
  error,
}: Props) {
  const initialBonusAmount = shiftData.shift?.bonus?.amount ?? 0
  const isSameAmount = newBonusAmount === initialBonusAmount
  const styles = useStyles(isSameAmount)
  const { theme } = useTheme()
  const minBonusAmount =
    shiftData.shift?.store.preferences?.minCriticalAmount ?? 5
  const maxBonusAmount =
    shiftData.shift?.store.preferences?.maxCriticalAmount ?? 100
  const decrementBonusAmount = 5
  const incrementBonusAmount = 5
  const errorMessage = error
    ? error.message
    : newBonusAmount - decrementBonusAmount < minBonusAmount
    ? t('decrementBonusError', { minAmount: minBonusAmount })
    : newBonusAmount + incrementBonusAmount > maxBonusAmount
    ? t('incrementBonusError', { maxAmount: maxBonusAmount })
    : null
  const BonusChanger = () => (
    <>
      <Icon
        onPress={() => setNewBonusAmount(newBonusAmount - decrementBonusAmount)}
        name={'minus'}
        type="font-awesome-5"
        solid
        color={theme.colors?.white}
        size={25}
        containerStyle={styles.buttonContainer}
        iconStyle={styles.buttonIcon}
        disabled={newBonusAmount - decrementBonusAmount < minBonusAmount}
      />
      <View style={styles.newBonusAmountContainer}>
        <Text style={styles.newBonusAmountText}>{`$${newBonusAmount}`}</Text>
      </View>
      <Icon
        name={'plus'}
        type="font-awesome-5"
        solid
        color={theme.colors?.white}
        size={25}
        containerStyle={styles.buttonContainer}
        iconStyle={styles.buttonIcon}
        onPress={() => setNewBonusAmount(newBonusAmount + incrementBonusAmount)}
        disabled={newBonusAmount + incrementBonusAmount > maxBonusAmount}
      />
    </>
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps={'handled'}
      >
        <View>
          <Text style={styles.primaryStatusMessage}>
            {t('changeIncentiveAmount')}
          </Text>
          <Text style={styles.secondaryStatusMessage}>
            {t('currentIncentiveAmount')}
          </Text>
        </View>
        <View style={{ backgroundColor: theme.colors?.white }}>
          <Text style={styles.secondaryStatusMessage}>
            $ {initialBonusAmount}
          </Text>
        </View>
        <View>
          <Text style={styles.primaryStatusMessage}>
            {t('increaseBonus')}
            {'\n'}
            {t('decreaseBonus')}
          </Text>
          <Text style={styles.secondaryStatusMessage}>
            {t('changeIncentive')}
          </Text>
        </View>
        <View style={styles.bonusChanger}>
          <BonusChanger />
        </View>
        {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}
        <View style={styles.buttonView}>
          <CommandButtons
            onButtonPress={onCommandButtonPress}
            commands={commands}
          ></CommandButtons>
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}

const useStyles = makeStyles((theme, isSameAmount) => ({
  root: {
    flex: 1,
  },
  container: {
    flexGrow: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.colors?.grey5,
  },

  primaryStatusMessage: {
    fontSize: 16,
    fontFamily: font.bold,
    textAlign: 'justify',
    padding: 10,
    color: theme.colors?.black,
  },
  secondaryStatusMessage: {
    fontSize: 15,
    fontFamily: font.normal,
    textAlign: 'justify',
    padding: 10,
    color: theme.colors?.grey0,
  },
  buttonView: {
    flex: 1,
    padding: 10,
  },
  bonusChanger: {
    backgroundColor: theme.colors?.white,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  buttonContainer: {
    backgroundColor: theme.colors?.black,
    borderRadius: 25,
    margin: 5,
  },
  buttonIcon: {
    paddingVertical: 12,
    paddingHorizontal: 15,
  },
  newBonusAmountContainer: {
    width: 100,
    height: 60,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    borderColor: theme.colors?.grey0,
  },
  newBonusAmountText: {
    fontSize: 25,
    color: isSameAmount ? theme.colors?.black : theme.colors?.primary,
  },
  error: {
    margin: 10,
    textAlign: 'center',
    color: theme.colors?.primary,
    fontFamily: font.bold,
    fontSize: 16,
  },
}))
